import React from 'react';
import styled from 'styled-components';
import Home from '../Home';
import LocalSEO2 from './LocalSEO2';
import LocalSeo3 from './LocalSeo3';
import LocalSEO4 from './LocalSEO4';

// --- Global Styles ---
const GlobalStyleContainer = styled.div`
  font-family: 'Inter', 'Segoe UI', Arial, sans-serif;
  color: #2d3748;
  margin: 0;
  padding: 0;
  box-sizing: border-box;
  background: linear-gradient(135deg, #f7f7f7 0%, #f0f4f8 100%);
  line-height: 1.6;
`;

// --- Header Section ---
const Header = styled.header`
  background: linear-gradient(135deg, 
    rgba(51, 51, 51, 0.9) 0%, 
    rgba(26, 32, 44, 0.95) 100%),
    url('https://via.placeholder.com/1200x600/333333/ffffff?text=Local+SEO+Background') center center / cover no-repeat;
  color: white;
  padding: 100px 5% 80px;
  position: relative;
  text-align: left;
  clip-path: polygon(0 0, 100% 0, 100% 90%, 0 100%);
`;

const HeaderContent = styled.div`
  position: relative;
  z-index: 10;
  max-width: 1200px;
  margin: 0 auto;
`;

const HeaderSubtitle = styled.h2`
  font-size: 1.3em;
  font-weight: 400;
  margin-bottom: 15px;
  letter-spacing: 1px;
  color: #63b3ed;
  text-transform: uppercase;
  font-weight: 600;
`;

const HeaderTitle = styled.h1`
  font-size: 3.2em;
  margin-bottom: 25px;
  text-transform: uppercase;
  font-weight: 800;
  line-height: 1.1;
  background: linear-gradient(135deg, #fff 0%, #e2e8f0 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const HeaderList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 30px 0;
`;

const HeaderListItem = styled.li`
  font-size: 1.15em;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  padding: 8px 0;
  
  &::before {
    content: '✓';
    color: #f6e05e;
    font-weight: bold;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    background: rgba(246, 224, 94, 0.2);
    border-radius: 50%;
    margin-right: 12px;
    flex-shrink: 0;
  }
`;

const HeaderCTA = styled.div`
  margin-top: 40px;
`;

const CTAButton = styled.button`
  background: linear-gradient(135deg, #4299e1 0%, #3182ce 100%);
  color: white;
  border: none;
  padding: 15px 35px;
  font-size: 1.1em;
  border-radius: 50px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(66, 153, 225, 0.3);
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(66, 153, 225, 0.4);
    background: linear-gradient(135deg, #3182ce 0%, #2c5aa0 100%);
  }
`;

const HeaderBlocks = styled.div`
  position: absolute;
  right: 5%;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  flex-direction: column;
  gap: 15px;
`;

const HeaderBlock = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 70px;
  height: 70px;
  font-size: 1.8em;
  font-weight: 800;
  color: white;
  background: ${props => props.color || '#333'};
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 12px;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.3);
  transition: transform 0.3s ease;
  
  &:hover {
    transform: scale(1.1) rotate(5deg);
  }
`;

// --- Main Content Section ---
const MainContent = styled.div`
  padding: 80px 5% 40px;
  max-width: 1200px;
  margin: 0 auto;
`;

const SectionTitle = styled.h2`
  text-align: center;
  font-size: 2.5em;
  margin-bottom: 20px;
  padding: 20px 0;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  font-weight: 700;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 100px;
    height: 4px;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    border-radius: 2px;
  }
`;

const SectionDescription = styled.p`
  text-align: center;
  font-size: 1.15em;
  color: #4a5568;
  margin-bottom: 60px;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.7;
`;

const ServicesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 30px;
  margin-bottom: 80px;
`;

const ServiceCard = styled.div`
  background: white;
  border: none;
  border-radius: 16px;
  padding: 30px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: ${props => props.accentColor || '#4299e1'};
  }
  
  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 12px 35px rgba(0, 0, 0, 0.15);
  }
`;

const CardIcon = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 20px;
  color: ${props => props.color || '#007bff'};
`;

const CardTitle = styled.h3`
  font-size: 1.3em;
  margin: 0;
  color: #2d3748;
  font-weight: 700;
`;

const CardText = styled.p`
  font-size: 1em;
  color: #718096;
  margin-top: 15px;
  line-height: 1.6;
`;

// --- Why Choose Us Section ---
const WhyChooseUsSection = styled.div`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 70px 5%;
  margin-top: 50px;
  border-radius: 20px;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: -50%;
    right: -50%;
    width: 100%;
    height: 200%;
    background: rgba(255, 255, 255, 0.1);
    transform: rotate(45deg);
  }
`;

const WhyChooseTitle = styled.h2`
  text-align: center;
  font-size: 2.2em;
  margin-bottom: 50px;
  color: white;
  font-weight: 700;
  position: relative;
  z-index: 2;
`;

const ReasonList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 25px;
  position: relative;
  z-index: 2;
`;

const ReasonItem = styled.div`
  display: flex;
  align-items: flex-start;
  background: rgba(255, 255, 255, 0.95);
  padding: 30px;
  border-radius: 16px;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease;
  border-left: 6px solid ${props => props.color || '#4299e1'};
  
  &:hover {
    transform: translateX(10px);
  }
`;

const ReasonNumber = styled.div`
  width: 50px;
  height: 50px;
  background: ${props => props.color || '#4299e1'};
  color: white;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 1.3em;
  font-weight: 800;
  margin-right: 20px;
  flex-shrink: 0;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
`;

const ReasonContent = styled.div`
  flex-grow: 1;
`;

const ReasonHeader = styled.h3`
  font-size: 1.3em;
  color: #2d3748;
  margin-bottom: 10px;
  font-weight: 700;
`;

const ReasonText = styled.p`
  font-size: 1.05em;
  color: #4a5568;
  margin: 0;
  line-height: 1.6;
`;

// --- The Main Component ---
const LocalSEO = () => {
  const companyName = "SRK Software";

  const services = [
    { title: "On-Page SEO", icon: "📄", color: "#e53e3e", text: "We audit and optimize every page of your website. This involves keyword research, title tags, content optimization, and internal linking to make it more appealing and relevant to search engines." },
    { title: "Google My Business Optimization", icon: "🏢", color: "#3182ce", text: "Claiming and optimizing your Google My Business profile is essential. We focus on updating your business information, photos, services, and managing your Q&A to attract qualified customers." },
    { title: "Online Reviews Management", icon: "⭐", color: "#d69e2e", text: "Monitor and encourage customer reviews across platforms like Yelp, Facebook, and Google. Online reviews build trust while review response strategies further connect your commitment to customer satisfaction." },
    { title: "Localized Content Creation", icon: "✍️", color: "#38a169", text: "Create valuable content tailored to your target location. This includes local blog posts, landing pages, and city/service pages that inform and engage users, boosting your website rankings." },
    { title: "Social Media Engagement", icon: "💬", color: "#d53f8c", text: "Active participation on local social media platforms is crucial. We manage your presence, monitor relevant conversations, and use geotargeted ads to maximize local reach." },
    { title: "Mobile Optimization", icon: "📱", color: "#805ad5", text: "Ensure your website is mobile-friendly, as most local searches happen on smartphones. Fast-loading, responsive designs provide a better experience for all users." },
    { title: "Local Link Building", icon: "🔗", color: "#319795", text: "Building local citations from directories, local news sites, and organizations. Links from trustworthy sources show search engines your website's authority and local search ranking." },
    { title: "Online Directories and Citations", icon: "📋", color: "#dd6b20", text: "We manage your directory listings (Yelp, Yellow Pages, etc.). Consistent NAP (Name, Address, Phone) information across all platforms is essential for accurate data and improved local search results." },
    { title: "Tracking and Analytics", icon: "📊", color: "#4a5568", text: "We use tools like Google Analytics and Google Search Console to track your local SEO performance. We provide detailed reports on what's working and where improvements are needed." },
  ];

  const reasons = [
    { num: 1, header: "Expertise with a Friendly Touch", text: "Our team consists of passionate local SEO specialists. We don't just meet your typical team goals; we're a friendly bunch who love helping businesses see success online.", color: "#e53e3e" },
    { num: 2, header: "Tailored Solutions with a Smile", text: `We believe in personalized service. ${companyName} professionals understand your unique needs and craft solutions that fit you like a comfy pair of shoes - perfectly.`, color: "#3182ce" },
    { num: 3, header: "Your Success is Our Mission", text: "When you partner with us, your success becomes our mission. We're not just a service provider; we're your dedicated ally in achieving your goals and growing your business.", color: "#38a169" },
    { num: 4, header: "Results with a Side of Support", text: "We're not about a 'set it and forget it' approach. We're in it for the long haul! We provide ongoing support, monitoring, and friendly faces to turn to whenever you need a helping hand.", color: "#d69e2e" },
  ];

  const reasonColors = ['#e53e3e', '#3182ce', '#38a169', '#d69e2e'];

  return (
    <Home>
      <GlobalStyleContainer>
        <Header>
          <HeaderContent>
            <HeaderSubtitle>Get Found By Shoppers In Local Results</HeaderSubtitle>
            <HeaderTitle>BEST LOCAL SEO SERVICES IN PATNA</HeaderTitle>
            <HeaderList>
              <HeaderListItem>Boost Local SEO Results Nearby Business Visibility Online</HeaderListItem>
              <HeaderListItem>Target Local Customers with Precision Using Local SEO</HeaderListItem>
              <HeaderListItem>Enhance Local Reputation and Trust Through SEO</HeaderListItem>
            </HeaderList>
            <HeaderCTA>
              <CTAButton>Get Your Free SEO Audit</CTAButton>
            </HeaderCTA>
            <HeaderBlocks>
               <HeaderBlock color="#3182ce">S</HeaderBlock>
              <HeaderBlock color="#e53e3c">E</HeaderBlock>
              <HeaderBlock color="#d69e2e">O</HeaderBlock>
            </HeaderBlocks>
          </HeaderContent>
        </Header>

        <MainContent>
          <SectionTitle>Our Local SEO Services</SectionTitle>
          <SectionDescription>
            Local SEO is about making your business more visible and attractive to the people who live right in your neighborhood, helping them find and choose you over the competition. This not only increases foot traffic but also helps improve search engine rankings.
          </SectionDescription>

          <ServicesGrid>
            {services.map((service, index) => (
              <ServiceCard key={index} accentColor={service.color}>
                <CardIcon color={service.color}>
                  <span style={{ fontSize: '2.2em' }}>{service.icon}</span>
                  <CardTitle>{service.title}</CardTitle>
                </CardIcon>
                <CardText>{service.text}</CardText>
              </ServiceCard>
            ))}
          </ServicesGrid>

          <WhyChooseUsSection>
            <WhyChooseTitle>Why Choose {companyName}?</WhyChooseTitle>
            <ReasonList>
              {reasons.map((reason, index) => (
                <ReasonItem key={reason.num} color={reasonColors[index]}>
                  <ReasonNumber color={reasonColors[index]}>{reason.num}</ReasonNumber>
                  <ReasonContent>
                    <ReasonHeader>{reason.header}</ReasonHeader>
                    <ReasonText>{reason.text}</ReasonText>
                  </ReasonContent>
                </ReasonItem>
              ))}
            </ReasonList>
          </WhyChooseUsSection>
        </MainContent>
      </GlobalStyleContainer>
      <LocalSEO2/>
      <LocalSEO4/>
      <LocalSeo3/>
    </Home>
  );
};

export default LocalSEO;